import React, { forwardRef } from "react";

/**
 * Labelled input with consistent styling + error/help text.
 * Replaces the repeated label+input+error blocks in the register flow and
 * the minimal bundel/InputField.jsx.
 *
 * Forwards its ref so it plugs straight into react-hook-form:
 *   <TextField label="Title" error={errors.title?.message} {...register("title")} />
 *
 * Pass `as="textarea"` for a multiline field.
 */
const fieldBase =
  "mt-1 block w-full shadow-sm sm:text-sm rounded-lg transition focus:ring-2 focus:ring-fairblue-500 focus:border-fairblue-500";

const TextField = forwardRef(function TextField(
  {
    label,
    error,
    help,
    required = false,
    as = "input",
    type,
    id,
    name,
    className = "",
    labelAddon,
    ...rest
  },
  ref,
) {
  const fieldId = id ?? name;
  const Tag = as;
  // Inputs must carry a `type` so the forms-plugin border styling (which keys
  // off input[type=…]) applies; without it the field renders borderless.
  const inputType = as === "input" ? type || "text" : type;
  const errorClasses = error
    ? "border-red-700 bg-red-50"
    : "border-gray-300";

  return (
    <div>
      {label && (
        <div className="flex items-center pr-2">
          <label
            htmlFor={fieldId}
            className="block text-sm font-medium text-gray-700"
          >
            {label}
            {required && <span className="text-red-600">*</span>}
          </label>
          {labelAddon}
        </div>
      )}
      <Tag
        id={fieldId}
        name={name}
        ref={ref}
        type={inputType}
        className={`${fieldBase} ${errorClasses} ${className}`}
        {...rest}
      />
      {help && <p className="mt-2 text-sm text-gray-500">{help}</p>}
      {error && <span className="text-sm text-red-700">{error}</span>}
    </div>
  );
});

export default TextField;
